/* assets/littleg-upload-outbox.js — LITTLEG-UPLOAD-OUTBOX-01
   Owns offline confirmation reconciliation only.
   It stores the minimum retry payload in IndexedDB and reuses the original request/idempotency key. */
(function (window, document) {
  'use strict';

  var VERSION = '2026-08-01-upload-outbox-01';
  if (window.__LITTLEG_UPLOAD_OUTBOX__ === VERSION) return;
  window.__LITTLEG_UPLOAD_OUTBOX__ = VERSION;

  var DB_NAME = 'littleg-family-private';
  var DB_VERSION = 1;
  var STORE_NAME = 'upload_confirmation_outbox';
  var MAX_ATTEMPTS_PER_SESSION = 3;
  var syncing = false;
  var sessionAttempts = new Map();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function clone(value, fallback) {
    try {
      if (typeof window.structuredClone === 'function') return window.structuredClone(value);
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return fallback;
    }
  }

  function openDb() {
    return new Promise(function (resolve, reject) {
      if (!window.indexedDB) {
        reject(new Error('indexeddb_unavailable'));
        return;
      }
      var request = window.indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function () {
        var db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          var store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('next_attempt_at', 'next_attempt_at', { unique: false });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error || new Error('indexeddb_open_failed')); };
    });
  }

  function getAll() {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readonly');
        var request = tx.objectStore(STORE_NAME).getAll();
        request.onsuccess = function () { resolve(Array.isArray(request.result) ? request.result : []); };
        request.onerror = function () { reject(request.error || new Error('indexeddb_get_all_failed')); };
        tx.oncomplete = function () { db.close(); };
        tx.onerror = function () { db.close(); };
      });
    });
  }

  function put(record) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(record);
        tx.oncomplete = function () { resolve(record); db.close(); };
        tx.onerror = function () { reject(tx.error || new Error('indexeddb_put_failed')); db.close(); };
      });
    });
  }

  function remove(id) {
    return openDb().then(function (db) {
      return new Promise(function (resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(id);
        tx.oncomplete = function () { resolve(); db.close(); };
        tx.onerror = function () { reject(tx.error || new Error('indexeddb_delete_failed')); db.close(); };
      });
    });
  }

  function responseConfirmation(response) {
    var data = response && response.data && typeof response.data === 'object' ? response.data : {};
    var source = data.confirmation && typeof data.confirmation === 'object'
      ? data.confirmation
      : data.record && typeof data.record === 'object'
        ? data.record
        : data;
    return source || {};
  }

  function isLocalOnly(response) {
    return clean(responseConfirmation(response).persistence) === 'local_only';
  }

  function isBackendConfirmed(response) {
    var source = responseConfirmation(response);
    return clean(source.persistence) === 'backend_confirmed' || clean(source.status) === 'private_backend_confirmed';
  }

  function sanitizeInput(input, response) {
    var source = input && typeof input === 'object' ? input : {};
    var confirmation = responseConfirmation(response);
    var requestId = clean(
      source.request_id ||
      response && response.request_id ||
      confirmation.request_id ||
      confirmation.record && confirmation.record.request_id
    );
    if (!requestId) return null;
    return {
      request_id: requestId,
      idempotency_key: clean(source.idempotency_key || source.idempotencyKey) || 'confirm:' + requestId,
      notice: clone(source.notice || {}, {}),
      packing_list: clone(source.packing_list || {}, {}),
      checklist: clone(Array.isArray(source.checklist) ? source.checklist : [], [])
    };
  }

  function backoffMs(attempts) {
    return Math.min(6 * 60 * 60 * 1000, Math.max(30 * 1000, Math.pow(2, Math.max(0, attempts - 1)) * 60 * 1000));
  }

  function root() {
    return document.querySelector('[data-littleg-upload-workspace]');
  }

  function setUi(state, count, detail) {
    var workspace = root();
    if (workspace) {
      workspace.setAttribute('data-upload-sync-state', state || 'idle');
      workspace.setAttribute('data-upload-outbox-count', String(Math.max(0, Number(count || 0))));
    }
    try {
      document.dispatchEvent(new CustomEvent('littleg:upload-sync-state', {
        detail: Object.assign({ state: state || 'idle', count: Number(count || 0), version: VERSION }, detail || {})
      }));
    } catch (_) {}
  }

  function refreshUi(state, detail) {
    return getAll().then(function (rows) {
      setUi(state || (rows.length ? 'pending' : 'idle'), rows.length, detail);
      return rows;
    }).catch(function () {
      setUi('error', 0, { code: 'outbox_unavailable' });
      return [];
    });
  }

  function enqueue(input, response) {
    var payload = sanitizeInput(input, response);
    if (!payload) return Promise.resolve(false);
    var now = Date.now();
    var record = {
      id: payload.request_id,
      request_id: payload.request_id,
      idempotency_key: payload.idempotency_key,
      payload: payload,
      attempts: 0,
      created_at: new Date(now).toISOString(),
      updated_at: new Date(now).toISOString(),
      next_attempt_at: now,
      last_error: ''
    };
    return put(record).then(function () {
      return refreshUi('pending', { request_id: payload.request_id }).then(function () { return true; });
    });
  }

  function recordFailure(record, error) {
    var attempts = Number(record.attempts || 0) + 1;
    record.attempts = attempts;
    record.updated_at = new Date().toISOString();
    record.next_attempt_at = Date.now() + backoffMs(attempts);
    record.last_error = clean(error && (error.code || error.message || error.status)) || 'sync_failed';
    return put(record);
  }

  async function syncRecord(record) {
    var transport = window.LittleGUploadTransport;
    if (!transport || typeof transport.confirmNotice !== 'function') throw new Error('transport_unavailable');
    var used = Number(sessionAttempts.get(record.id) || 0);
    if (used >= MAX_ATTEMPTS_PER_SESSION) return false;
    sessionAttempts.set(record.id, used + 1);

    var payload = Object.assign({}, clone(record.payload, {}), {
      request_id: record.request_id,
      idempotency_key: record.idempotency_key,
      __outbox_sync: true
    });
    var response = await transport.confirmNotice(payload);
    if (!isBackendConfirmed(response) || isLocalOnly(response)) {
      var error = new Error('backend_not_confirmed');
      error.code = 'backend_not_confirmed';
      throw error;
    }
    await remove(record.id);
    return true;
  }

  async function drain() {
    if (syncing || window.navigator.onLine === false) return;
    syncing = true;
    try {
      var rows = await refreshUi('syncing');
      rows.sort(function (left, right) { return Number(left.next_attempt_at || 0) - Number(right.next_attempt_at || 0); });
      for (var index = 0; index < rows.length; index += 1) {
        var record = rows[index];
        if (Number(record.next_attempt_at || 0) > Date.now()) continue;
        try {
          await syncRecord(record);
        } catch (error) {
          await recordFailure(record, error).catch(function () {});
        }
      }
      var remaining = await getAll();
      setUi(remaining.length ? 'error' : 'synced', remaining.length);
    } catch (error) {
      await refreshUi('error', { code: clean(error && (error.code || error.message)) || 'sync_failed' });
    } finally {
      syncing = false;
    }
  }

  function onTransportSuccess(event) {
    var detail = event && event.detail ? event.detail : {};
    if (detail.method !== 'confirmNotice' || detail.input && detail.input.__outbox_sync) return;
    if (!isLocalOnly(detail.response)) return;
    enqueue(detail.input, detail.response).catch(function () { setUi('error', 0, { code: 'outbox_enqueue_failed' }); });
  }

  function boot() {
    refreshUi().then(function () {
      if (window.navigator.onLine !== false) drain();
    });
  }

  window.LittleGUploadOutbox = Object.freeze({
    VERSION: VERSION,
    enqueue: enqueue,
    drain: drain,
    list: getAll
  });

  document.addEventListener('littleg:upload-transport-success', onTransportSuccess);
  document.addEventListener('littleg:upload-transport-ready', function () { drain(); });
  window.addEventListener('online', function () { drain(); });
  window.addEventListener('pageshow', function () { drain(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') drain();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})(window, document);
